/**
 * HarvestService.ts — Redux thunks for the CampMinder harvest flow.
 *
 * Follows the service thunk pattern established in DashboardChatService.ts.
 * Calls HarvestAPI (pure HTTP wrappers) and dispatches Redux actions.
 * Components never call HarvestAPI directly — they dispatch these thunks.
 *
 * Spec: §3d.9 + §3d.2 (pre-harvest picker sequencing)
 */
import type { AppDispatch, RootState } from '@/redux'
import { HarvestAPI } from '@/API/Harvest'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { DashboardSocketService } from '@/services/DashboardSocketService'

// ─── startHarvestThunk ────────────────────────────────────────────────────────

/**
 * Initiates a harvest session for the given appCode.
 * On success: stores sessionId + liveViewUrl in Redux, transitions modal to 'login'.
 * On error: clears harvest state and sets harvestFailure with the error code.
 */
export const startHarvestThunk =
  (token: string, appCode: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const res = await HarvestAPI.start(token, { appCode })

    if (res.err || !res.data) {
      dispatch(
        dashboardCreationActions.setHarvestFailure({
          code: res.err ?? 'START_FAILED',
          partialSuccess: false,
        })
      )
      return
    }

    dispatch(dashboardCreationActions.setHarvestSessionId(res.data.sessionId))
    dispatch(dashboardCreationActions.setHarvestLiveViewUrl(res.data.liveViewUrl))
    // Transition to login state — iframe becomes visible, trust strip shown
    dispatch(dashboardCreationActions.setHarvestContentState('login'))
    // Join the harvest session room so the backend's HarvestProgressGateway
    // can deliver events to this client. Use the dedicated method (not raw
    // emit) so the session id is tracked and auto-rejoined on reconnect —
    // CEO live-validation 2026-04-28: a raw emit silently no-ops if the
    // socket isn't yet connected, leaving every harvest event in the
    // room with socketsInRoom=0 and the modal stuck in 'login' state.
    DashboardSocketService.joinHarvestSession(res.data.sessionId)
  }

// ─── selectClientIdThunk ─────────────────────────────────────────────────────

/**
 * Pre-harvest camp selection (v3 Plaid-style — §3d.9).
 *
 * Successful select-client-id transitions the modal back to 'harvest' state
 * (iframe re-shown, progress narration begins). The 'success' state arrives
 * later via the harvest:progress Socket.IO event when the backend reaches
 * state `complete` (after key scrape + JWT validation). Caller must continue
 * listening for harvest:progress events.
 */
export const selectClientIdThunk =
  (token: string, sessionId: string, clientId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const res = await HarvestAPI.selectClientId(token, sessionId, clientId)

    if (res.err || !res.data) {
      dispatch(
        dashboardCreationActions.setHarvestFailure({
          code: res.err ?? 'SELECT_CAMP_FAILED',
          partialSuccess: false,
        })
      )
      return
    }

    dispatch(
      dashboardCreationActions.setHarvestSelectedClientIdPreharvest(
        res.data.selectedClientIdPreharvest
      )
    )
    // Transition back to harvest state — iframe becomes visible again,
    // progress narration overlay begins. §3d.2: "iframe re-shown during harvest"
    dispatch(dashboardCreationActions.setHarvestContentState('harvest'))
  }

// ─── terminateHarvestThunk ───────────────────────────────────────────────────

/**
 * User-initiated cancel. Backend determines failureReason based on prior state.
 * Called when user clicks Cancel in the camp picker or dismisses the modal.
 *
 * Always dispatches `setHarvestFailure` after terminating:
 *  - `code: 'user_cancelled_camp_picker'` when terminated from the picking_camp
 *    state (auth memo §4.7 — shows the "No problem, we didn't create anything"
 *    copy variant in the chat surface).
 *  - `code: 'user_abandoned'` in all other states (generic mid-flow copy variant).
 *
 * This keeps the modal closed while the chat surface picks the correct copy
 * variant from `harvestFailure.code`.
 */
export const terminateHarvestThunk =
  (token: string, sessionId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const contentState = getState().dashboardCreation.harvestContentState

    try {
      await HarvestAPI.terminate(token, sessionId)
    } catch (err) {
      console.warn('terminateHarvestThunk: API termination failed', err)
      // Fall through — user-facing cleanup must always run even if the server
      // never received the termination request (network error, etc.).
    }

    // Leave the harvest socket room so a future reconnect doesn't auto-rejoin
    // the now-terminated session and accept stale events into the next harvest.
    DashboardSocketService.leaveHarvestSession()

    // Clear harvest state so the modal can be re-opened cleanly
    dispatch(dashboardCreationActions.clearHarvest())

    // If terminated from picking_camp, the chat surface shows the friendly
    // "No problem — we didn't create anything yet" copy (per auth memo §4.7).
    // Flag the code so the chat bubble can select the correct copy variant.
    if (contentState === 'picking_camp') {
      dispatch(
        dashboardCreationActions.setHarvestFailure({
          code: 'user_cancelled_camp_picker',
          partialSuccess: false,
        })
      )
    } else {
      dispatch(
        dashboardCreationActions.setHarvestFailure({
          code: 'user_abandoned',
          partialSuccess: false,
        })
      )
    }
  }
